export type ModalSize = "compact" | "default" | "wide";

export interface ModalProperties {
  title?: string;
  subtitle?: string;
  description?: string;
  form?: string;
  size?: ModalSize;
  formProps?: any;
}

/*****************/
/* INITIAL STATE */
/*****************/

type ModalState = {
  modalStack: ModalProperties[];
  modal: ModalProperties | null | undefined;
  childModal: ModalProperties | null | undefined;
};

const initialState: ModalState = {
  modalStack: [],
  modal: null,
  childModal: null,
};

/*********/
/* TYPES */
/*********/

enum ModalActionTypes {
  SHOW_MODAL = "SHOW_MODAL",
  HIDE_MODAL = "HIDE_MODAL",
  SHOW_CHILD_MODAL = "SHOW_CHILD_MODAL",
  HIDE_CHILD_MODAL = "HIDE_CHILD_MODAL",
  HIDE_ALL_MODALS = "HIDE_ALL_MODALS",
}

export interface ModalAction {
  type: ModalActionTypes;
  payload?: ModalProperties;
}

/*******************/
/* ACTION CREATORS */
/*******************/

export function showModal(payload: ModalProperties): ModalAction {
  return {
    type: ModalActionTypes.SHOW_MODAL,
    payload,
  };
}

export function hideModal(): ModalAction {
  return {
    type: ModalActionTypes.HIDE_MODAL,
  };
}

export function showChildModal(payload: ModalProperties): ModalAction {
  return {
    type: ModalActionTypes.SHOW_CHILD_MODAL,
    payload,
  };
}

export function hideChildModal(): ModalAction {
  return {
    type: ModalActionTypes.HIDE_CHILD_MODAL,
  };
}

export function hideAllModals(): ModalAction {
  return {
    type: ModalActionTypes.HIDE_ALL_MODALS,
  };
}

/***********/
/* HELPERS */
/***********/

function getCurrentModal(modalStack: ModalProperties[]) {
  return modalStack.length > 0 ? modalStack[0] : null;
}

function getCurrentChildModal(modalStack: ModalProperties[]) {
  return modalStack.length > 1 ? modalStack[modalStack.length - 1] : null;
}

/***********/
/* REDUCER */
/***********/

function modalReducer(state = initialState, action: ModalAction): ModalState {
  switch (action.type) {
    case ModalActionTypes.SHOW_MODAL: {
      const nextStack = action.payload ? [action.payload] : [];

      return {
        ...state,
        modalStack: nextStack,
        modal: getCurrentModal(nextStack),
        childModal: getCurrentChildModal(nextStack),
      };
    }

    case ModalActionTypes.SHOW_CHILD_MODAL: {
      const nextStack = action.payload
        ? [...state.modalStack, action.payload]
        : state.modalStack;

      return {
        ...state,
        modalStack: nextStack,
        modal: getCurrentModal(nextStack),
        childModal: getCurrentChildModal(nextStack),
      };
    }

    case ModalActionTypes.HIDE_MODAL:
      return {
        ...state,
        modalStack: [],
        modal: null,
        childModal: null,
      };

    case ModalActionTypes.HIDE_CHILD_MODAL: {
      const nextStack =
        state.modalStack.length > 1
          ? state.modalStack.slice(0, -1)
          : state.modalStack;

      return {
        ...state,
        modalStack: nextStack,
        modal: getCurrentModal(nextStack),
        childModal: getCurrentChildModal(nextStack),
      };
    }

    case ModalActionTypes.HIDE_ALL_MODALS:
      return {
        ...state,
        modalStack: [],
        modal: null,
        childModal: null,
      };

    default:
      return state;
  }
}

export default modalReducer;